<script setup lang="ts">
import { DateTime } from 'luxon';
import OverlayPanel from 'primevue/overlaypanel';
import SelectButton from 'primevue/selectbutton';
import Skeleton from 'primevue/skeleton';
import { computed, onMounted, ref, shallowRef, useAttrs, watch } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import {
  CalendarDate,
  CalendarEmits,
  CalendarMonthOptions,
  CalendarProps,
  CalendarYearOptions,
} from './Calendar.vue.d';
import Button from '../button/Button.vue';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import Icon from '../icon/Icon.vue';
import InputGroup from '../inputgroup/InputGroup.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<CalendarProps>(), {
  selectionMode: 'single',
  view: 'date',
  mode: 'single',
  showYear: true,
  showOptionalText: undefined,
  valueFormat: 'millis',
  hideOnRangeSelection: true,

  hourFormat: '24',
  dateFormat: 'dd/mm/yy',
  stepHour: 1,
  stepMinute: 1,
  timeSeparator: ':',

  onlyWeekdays: false,
  excludeDates: () => [],
  exactSelection: false,
});

const emit = defineEmits<CalendarEmits>();

onMounted(() => {
  updateCalendar();
});

const attrs = useAttrs();
const config = useWangsVueConfig('Calendar');

const ticksTo1970 =
  ((1970 - 1) * 365 +
    Math.floor(1970 / 4) -
    Math.floor(1970 / 100) +
    Math.floor(1970 / 400)) *
  24 *
  60 *
  60 *
  10000000;

const input = ref<HTMLInputElement>();
const overlayPanel = ref<OverlayPanel>();
const wrapper = ref<typeof FieldWrapper>();

const currentDate = new Date();
const currentMonth = ref(currentDate.getMonth());
const currentYear = ref(currentDate.getFullYear());
const currentHour = ref(currentDate.getHours());
const currentMinute = ref(currentDate.getMinutes());
const currentSecond = ref(currentDate.getSeconds());
const temporaryDate = ref<Date | Date[]>(props.modelValue);
const changesSaved = ref<boolean>(false);

const inputValue = shallowRef<string>();
const currentView = shallowRef<typeof props.view>(props.view ?? 'date');
const weeksInMonth = shallowRef<CalendarDate[][]>([]);
const isLoading = shallowRef<boolean>(false);
const pm = shallowRef<boolean>(false);
const timePickerTimer = shallowRef<NodeJS.Timeout>();
const timePickerChange = shallowRef<boolean>(false);

const previousDateMeta = shallowRef<CalendarDate>();
const previousDatesInMonth = shallowRef<CalendarDate[]>([]);

const hideNavigator = computed(
  () => currentView.value === 'month' && !props.showYear,
);

const calendarModelValue = computed(() => {
  return props.showButtons || props.showTime
    ? temporaryDate.value
    : props.modelValue;
});

const datesInMonth = computed(() => {
  return weeksInMonth.value.flatMap((week) => week);
});

const formattedCurrentHour = computed(() => {
  return currentHour.value < 10 ? '0' + currentHour.value : currentHour.value;
});

const formattedCurrentMinute = computed(() => {
  return currentMinute.value < 10
    ? '0' + currentMinute.value
    : currentMinute.value;
});

const datePattern = computed(() => {
  return props.dateFormat || config.locale.global.dateFormat;
});

const monthPickerValues = computed(() => {
  const months: CalendarMonthOptions[] = [];

  const isSelectableMonth = (baseMonth: number): boolean => {
    if (props.minDate) {
      const minMonth = props.minDate.getMonth();
      const minYear = props.minDate.getFullYear();

      if (
        currentYear.value < minYear ||
        (currentYear.value === minYear && baseMonth < minMonth)
      ) {
        return false;
      }
    }

    if (props.maxDate) {
      const maxMonth = props.maxDate.getMonth();
      const maxYear = props.maxDate.getFullYear();

      if (
        currentYear.value > maxYear ||
        (currentYear.value === maxYear && baseMonth > maxMonth)
      ) {
        return false;
      }
    }

    if (props.maxMonth) {
      if (props.maxMonth === 'current') {
        const today = new Date();
        const monthInPresent = today.getMonth();
        const yearInPresent = today.getFullYear();
        return (
          (baseMonth <= monthInPresent && currentYear.value == yearInPresent) || // In current year
          currentYear.value < yearInPresent // In past year
        );
      }
    }

    return true;
  };

  for (let i = 0; i <= 11; i++) {
    months.push({
      value: i,
      selectable: isSelectableMonth(i),
    });
  }

  return months;
});

const yearPickerValues = computed(() => {
  const years: CalendarYearOptions[] = [];
  const base = currentYear.value - (currentYear.value % 10);

  const isSelectableYear = (baseYear: number): boolean => {
    if (props.minDate) {
      if (props.minDate.getFullYear() > baseYear) return false;
    }

    if (props.maxDate) {
      if (props.maxDate.getFullYear() < baseYear) return false;
    }

    if (props.maxYear) {
      if (props.maxYear === 'current') {
        const yearInPresent = new Date().getFullYear();
        return baseYear <= yearInPresent;
      }
    }

    return true;
  };

  for (let i = 0; i < 10; i++) {
    years.push({
      value: base + i,
      selectable: isSelectableYear(base + i),
    });
  }

  return years;
});

// The date that should be shown when opening the calendar
const viewDate = computed(() => {
  let propValue: Date;

  if (calendarModelValue.value && isArraySelection(calendarModelValue.value)) {
    propValue =
      calendarModelValue.value[getLastIndex(calendarModelValue.value)] ||
      calendarModelValue.value[0];
  } else {
    propValue = calendarModelValue.value as Date;
  }

  if (
    propValue &&
    typeof propValue !== 'string' &&
    propValue.toString() !== 'Invalid Date'
  ) {
    return propValue;
  }

  const today = new Date();

  if (props.maxDate && props.maxDate < today) {
    return props.maxDate;
  }

  if (props.minDate && props.minDate > today) {
    return props.minDate;
  }

  return today;
});

const invalidInput = computed(() => props.invalid || !!attrs['error-message']);

/**
 * Instead directly assign index like this `arr[1]`, adjust index to be more flexible while keeping its original behavior (default return 1)
 * @param date
 */
const getLastIndex = (date: Date[]): number => {
  if (date.length > 2) return date.length - 1;
  return 1;
};

const getWeeksInMonth = (): CalendarDate[][] => {
  const firstDayOfMonth = new Date(currentYear.value, currentMonth.value, 1);
  const lastDayOfMonth = new Date(currentYear.value, currentMonth.value + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const lastDayOfWeek = lastDayOfMonth.getDay();

  const weeks: CalendarDate[][] = [];
  let currentDay = 1 - firstDayOfWeek;

  while (currentDay <= daysInMonth) {
    const week: CalendarDate[] = [];

    for (let i = 0; i < 7; i++) {
      week.push({
        day: currentDay,
        month: currentMonth.value,
        year: currentYear.value,
        otherMonth: currentDay < 1 || currentDay > daysInMonth,
        today:
          currentDay === currentDate.getDate() &&
          currentMonth.value === new Date().getMonth() &&
          currentYear.value === new Date().getFullYear(),
        selectable: isSelectable(
          currentDay,
          currentMonth.value,
          currentYear.value,
        ),
      });

      currentDay++;
    }

    weeks.push(week);
  }

  // Fill the last week with next month's days if needed
  if (lastDayOfWeek !== 6) {
    const lastWeek = weeks[weeks.length - 1];
    let nextMonthDay = 1;
    for (let i = lastDayOfWeek + 1; i < 7; i++) {
      lastWeek[i].day = nextMonthDay++;
      lastWeek[i].otherMonth = true;
    }
  }

  return weeks;
};

const prevMonth = (): void => {
  if (currentMonth.value === 0) {
    currentYear.value--;
    currentMonth.value = 11;
  } else {
    currentMonth.value--;
  }

  updateCalendar();
};

const decrementYear = (): void => {
  currentYear.value--;
};

const incrementYear = (): void => {
  currentYear.value++;
};

const nextMonth = (): void => {
  if (currentMonth.value === 11) {
    incrementYear();
    currentMonth.value = 0;
  } else {
    currentMonth.value++;
  }

  updateCalendar();
};

const decrementDecade = (): void => {
  currentYear.value -= 10;
};

const incrementDecade = (): void => {
  currentYear.value += 10;
};

const navBackward = (): void => {
  if (currentView.value === 'date') {
    prevMonth();
  } else if (currentView.value === 'month') {
    decrementYear();
  } else {
    decrementDecade();
  }
};

const navForward = (): void => {
  if (currentView.value === 'date') {
    nextMonth();
  } else if (currentView.value === 'month') {
    incrementYear();
  } else {
    incrementDecade();
  }
};

const updateCalendar = (): void => {
  weeksInMonth.value = getWeeksInMonth();
};

const isComparable = (): boolean => {
  return (
    calendarModelValue.value != null &&
    typeof calendarModelValue.value !== 'string'
  );
};

const isSingleSelection = (
  value: typeof calendarModelValue.value,
): value is Date | null => {
  return (
    props.selectionMode === 'single' &&
    (!Array.isArray(calendarModelValue.value) ||
      calendarModelValue.value == null)
  );
};

const isRangeSelection = (): boolean | null => {
  return (
    props.selectionMode === 'range' &&
    (Array.isArray(calendarModelValue.value) ||
      calendarModelValue.value == null)
  );
};

const isMultipleSelection = (): boolean | null => {
  return (
    props.selectionMode === 'multiple' &&
    (Array.isArray(calendarModelValue.value) ||
      calendarModelValue.value == null)
  );
};

const isArraySelection = (
  value: typeof calendarModelValue.value,
): value is Date[] => {
  return isMultipleSelection() || isRangeSelection();
};

const isDateEquals = (value: Date | null, dateMeta: CalendarDate): boolean => {
  if (value)
    return (
      value.getDate() === dateMeta.day &&
      value.getMonth() === dateMeta.month &&
      value.getFullYear() === dateMeta.year
    );
  return false;
};

const isDateBetween = (
  start: Date | null,
  end: Date | null,
  dateMeta: CalendarDate,
): boolean => {
  const between = false;

  if (start && end) {
    const date = new Date(dateMeta.year, dateMeta.month, dateMeta.day);

    return start.getTime() <= date.getTime() && end.getTime() >= date.getTime();
  }

  return between;
};

const isSelected = (dateMeta: CalendarDate): boolean => {
  if (!isComparable()) {
    return false;
  }

  if (calendarModelValue.value) {
    if (isSingleSelection(calendarModelValue.value)) {
      return isDateEquals(calendarModelValue.value, dateMeta);
    } else if (isMultipleSelection()) {
      return calendarModelValue.value.some((dateIter) =>
        isDateEquals(dateIter, dateMeta),
      );
    } else if (isRangeSelection()) {
      if (calendarModelValue.value[getLastIndex(calendarModelValue.value)])
        return (
          isDateEquals(calendarModelValue.value[0], dateMeta) ||
          isDateEquals(
            calendarModelValue.value[getLastIndex(calendarModelValue.value)],
            dateMeta,
          ) ||
          isDateBetween(
            calendarModelValue.value[0],
            calendarModelValue.value[getLastIndex(calendarModelValue.value)],
            dateMeta,
          )
        );

      return isDateEquals(calendarModelValue.value[0], dateMeta);
    }
  }

  return false;
};

const isFirstSelected = (dateMeta: CalendarDate): boolean => {
  if (isSingleSelection(calendarModelValue.value) || isMultipleSelection())
    return isSelected(dateMeta);
  else if (isRangeSelection())
    return (
      isSelected(dateMeta) &&
      (isDateEquals(calendarModelValue.value[0], dateMeta) ||
        calendarModelValue.value[0] == null)
    );
  return false;
};

const isLastSelected = (dateMeta: CalendarDate): boolean => {
  if (isSingleSelection(calendarModelValue.value) || isMultipleSelection())
    return isSelected(dateMeta);
  else if (isRangeSelection())
    return (
      isSelected(dateMeta) &&
      (isDateEquals(
        calendarModelValue.value[getLastIndex(calendarModelValue.value)],
        dateMeta,
      ) ||
        calendarModelValue.value[getLastIndex(calendarModelValue.value)] ==
          null)
    );
  return false;
};

const isMonthSelected = (month: number): boolean => {
  if (isComparable()) {
    if (isSingleSelection(calendarModelValue.value)) {
      return (
        calendarModelValue.value.getMonth() === month &&
        calendarModelValue.value.getFullYear() === currentYear.value
      );
    }
    if (isMultipleSelection())
      return calendarModelValue.value.some(
        (date) =>
          date.getMonth() === month && date.getFullYear() === currentYear.value,
      );
    if (isRangeSelection()) {
      const [start, end] = calendarModelValue.value;
      if (start && end) {
        const startDate = new Date(start.getFullYear(), start.getMonth());
        const endDate = new Date(end.getFullYear(), end.getMonth());
        const currentMonthDate = new Date(currentYear.value, month);
        return currentMonthDate >= startDate && currentMonthDate <= endDate;
      }
    }
  }

  return false;
};

const isYearSelected = (year: number): boolean => {
  if (isComparable()) {
    if (isSingleSelection(calendarModelValue.value))
      return calendarModelValue.value?.getFullYear() === year;
    if (isMultipleSelection())
      return calendarModelValue.value.some(
        (date) => date.getFullYear() === year,
      );
    if (isRangeSelection()) {
      const [start, end] = calendarModelValue.value;
      return (
        year >= start.getFullYear() && year <= (end ?? start).getFullYear()
      );
    }
  }

  return false;
};

const isSelectable = (day: number, month: number, year: number): boolean => {
  let validMin = true;
  let validMax = true;
  let validWeekdays = true;
  let validExcludes = true;

  if (props.onlyWeekdays) {
    const dayOfWeek = new Date(year, month, day).getDay();
    // Only allow Monday to Friday
    validWeekdays = dayOfWeek >= 1 && dayOfWeek <= 5;
  }

  if (props.excludeDates) {
    validExcludes = !props.excludeDates.some(
      (excludeDate) =>
        excludeDate.getFullYear() === year &&
        excludeDate.getMonth() === month &&
        excludeDate.getDate() === day,
    );
  }

  if (props.minDate) {
    if (props.minDate.getFullYear() > year) {
      validMin = false;
    } else if (props.minDate.getFullYear() === year) {
      if (props.minDate.getMonth() > month) {
        validMin = false;
      } else if (props.minDate.getMonth() === month) {
        if (props.minDate.getDate() > day) {
          validMin = false;
        }
      }
    }
  }

  if (props.maxDate) {
    if (props.maxDate.getFullYear() < year) {
      validMax = false;
    } else if (props.maxDate.getFullYear() === year) {
      if (props.maxDate.getMonth() < month) {
        validMax = false;
      } else if (props.maxDate.getMonth() === month) {
        if (props.maxDate.getDate() < day) {
          validMax = false;
        }
      }
    }
  }

  return validMin && validMax && validWeekdays && validExcludes;
};

const onDateSelect = (event: Event, date: CalendarDate): void => {
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.focus();
  }

  if (date.selectable) selectDate(date);

  if (
    props.selectionMode === 'single' &&
    (!props.showTime || props.hideOnDateTimeSelect)
  ) {
    hidePanel();
  }
};

const onMonthSelect = (event: Event, monthMeta: CalendarMonthOptions): void => {
  if (!monthMeta.selectable) return;

  if (props.view === 'month') {
    onDateSelect(event, {
      year: currentYear.value,
      month: monthMeta.value,
      day: 1,
      selectable: true,
      today: false,
      otherMonth: false,
    });
  } else {
    currentMonth.value = monthMeta.value;
    currentView.value = 'date';

    emit('monthChange', {
      month: currentMonth.value,
      year: currentYear.value,
    });
  }

  updateCalendar();
};

const onYearSelect = (event: Event, year: CalendarYearOptions): void => {
  if (!year.selectable) return;

  if (props.view === 'year') {
    onDateSelect(event, {
      year: year.value,
      month: 0,
      day: 1,
      selectable: true,
      otherMonth: false,
      today: false,
    });
  } else {
    currentYear.value = year.value;
    currentView.value = 'month';
    emit('yearChange', {
      month: currentMonth.value + 1,
      year: currentYear.value,
    });
  }

  updateCalendar();
};

/**
 * Handles the mouse down event on the time picker element.
 *
 * @param {Event} event - The mouse down event.
 * @param {0 | 1 | 2} type - The type of time unit being adjusted (0 for Hour, 1 for Minute, 2 for Second).
 * @param {-1 | 1} direction - The direction of adjustment (1 for up, -1 for down).
 * @returns {void}
 */
const onTimePickerElementMouseDown = (
  event: Event,
  type: 0 | 1 | 2,
  direction: -1 | 1,
): void => {
  if (!props.disabled && !props.readonly) {
    repeat(event, null, type, direction);
    event.preventDefault();
  }
};

const onTimePickerElementMouseUp = (event: Event): void => {
  if (!props.disabled && !props.readonly) {
    clearTimePickerTimer();
    updateModelTime();
    event.preventDefault();
  }
};

const onTimePickerElementMouseLeave = (): void => {
  clearTimePickerTimer();
};

const onContainerButtonKeydown = (event: KeyboardEvent): void => {
  switch (event.code) {
    case 'Tab':
      // TrapFocus(event);
      break;

    case 'Escape':
      hidePanel();
      event.preventDefault();
      break;

    default:
      //Noop
      break;
  }

  // $emit('keydown', event);
};

const onInputClick = (event: Event): void => {
  if (!props.disabled) {
    setTimeout(() => {
      // TO prevent overlay panel immediately closed after shown when programatically trigger input click
      overlayPanel.value.show(event, getOverlayTarget(event));
    }, 0);
  }
};

const onButtonTriggerClick = (event: Event): void => {
  if (!props.disabled) {
    setTimeout(() => {
      overlayPanel.value?.toggle(event, getOverlayTarget(event));
    }, 0);
  }
};

const onPanelHide = (): void => {
  currentView.value = props.view;
  updateCurrentMetaData();

  if (!changesSaved.value) {
    // Revert changes when the overlay is closed not by apply button
    revertChanges();
  }
};

const getOverlayTarget = (event: Event): HTMLElement => {
  return (
    ('$el' in wrapper.value ? wrapper.value.$el : input.value) ??
    event.currentTarget
  );
};

const repeat = (
  event: Event,
  interval: number,
  type: 0 | 1 | 2,
  direction: -1 | 1,
): void => {
  interval ||= 500;

  clearTimePickerTimer();
  timePickerTimer.value = setTimeout(() => {
    repeat(event, 100, type, direction);
  }, interval);

  switch (type) {
    case 0:
      if (direction === 1) incrementHour(event);
      else decrementHour(event);
      break;

    case 1:
      if (direction === 1) incrementMinute(event);
      else decrementMinute(event);
      break;
  }
};

const incrementHour = (event: Event): void => {
  const prevHour = currentHour.value;
  let newHour = currentHour.value + Number(props.stepHour);
  let newPM = pm.value;

  if (props.hourFormat == '24')
    newHour = newHour >= 24 ? newHour - 24 : newHour;
  else if (props.hourFormat == '12') {
    // Before the AM/PM break, now after
    if (prevHour < 12 && newHour > 11) {
      newPM = !pm.value;
    }

    newHour = newHour >= 13 ? newHour - 12 : newHour;
  }

  if (validateTime(newHour, currentMinute.value, currentSecond.value, newPM)) {
    currentHour.value = newHour;
    pm.value = newPM;
  }

  event.preventDefault();
};

const decrementHour = (event: Event): void => {
  let newHour = currentHour.value - props.stepHour;
  let newPM = pm.value;

  if (props.hourFormat == '24') newHour = newHour < 0 ? 24 + newHour : newHour;
  else if (props.hourFormat == '12') {
    // If we were at noon/midnight, then switch
    if (currentHour.value === 12) {
      newPM = !pm.value;
    }

    newHour = newHour <= 0 ? 12 + newHour : newHour;
  }

  if (validateTime(newHour, currentMinute.value, currentSecond.value, newPM)) {
    currentHour.value = newHour;
    pm.value = newPM;
  }

  event.preventDefault();
};

const incrementMinute = (event: Event): void => {
  const newMinute = currentMinute.value + Number(props.stepMinute);

  if (
    validateTime(currentHour.value, newMinute, currentSecond.value, pm.value)
  ) {
    currentMinute.value = newMinute > 59 ? newMinute - 60 : newMinute;
  }

  event.preventDefault();
};

const decrementMinute = (event: Event): void => {
  let newMinute = currentMinute.value - props.stepMinute;

  newMinute = newMinute < 0 ? 60 + newMinute : newMinute;

  if (
    validateTime(currentHour.value, newMinute, currentSecond.value, pm.value)
  ) {
    currentMinute.value = newMinute;
  }

  event.preventDefault();
};

const clearTimePickerTimer = (): void => {
  if (timePickerTimer.value) {
    clearInterval(timePickerTimer.value);
  }
};

const validateTime = (
  hour: number,
  minute: number,
  second: number,
  isPm: boolean,
): boolean => {
  let value: Date;
  const convertedHour = convertTo24Hour(hour, isPm);

  if (isArraySelection(calendarModelValue.value)) {
    const modelValue = isComparable()
      ? calendarModelValue.value
      : viewDate.value;
    value = modelValue[getLastIndex(modelValue as Date[])] || modelValue[0];
  } else {
    value = isComparable() ? calendarModelValue.value : viewDate.value;
  }

  const valueDateString = value ? value.toDateString() : null;

  if (
    props.minDate &&
    valueDateString &&
    props.minDate.toDateString() === valueDateString
  ) {
    if (props.minDate.getHours() > convertedHour) {
      return false;
    }

    if (props.minDate.getHours() === convertedHour) {
      if (props.minDate.getMinutes() > minute) {
        return false;
      }

      if (props.minDate.getMinutes() === minute) {
        if (props.minDate.getSeconds() > second) {
          return false;
        }
      }
    }
  }

  if (
    props.maxDate &&
    valueDateString &&
    props.maxDate.toDateString() === valueDateString
  ) {
    if (props.maxDate.getHours() < convertedHour) {
      return false;
    }

    if (props.maxDate.getHours() === convertedHour) {
      if (props.maxDate.getMinutes() < minute) {
        return false;
      }

      if (props.maxDate.getMinutes() === minute) {
        if (props.maxDate.getSeconds() < second) {
          return false;
        }
      }
    }
  }

  return true;
};

const convertTo24Hour = (hours: number, isPm: boolean): number => {
  if (props.hourFormat == '12') {
    if (hours === 12) return isPm ? 12 : 0;
    return isPm ? hours + 12 : hours;
  }

  return hours;
};

const updateModelTime = (): void => {
  timePickerChange.value = true;
  let value: Date | Date[];

  if (isArraySelection(calendarModelValue.value)) {
    const modelValue = isComparable()
      ? calendarModelValue.value
      : viewDate.value;
    value = modelValue[getLastIndex(modelValue as Date[])] || modelValue[0];
  } else {
    value = isComparable() ? calendarModelValue.value : viewDate.value;
  }

  value =
    value && !Array.isArray(value) ? new Date(value.getTime()) : new Date();

  if (props.hourFormat == '12') {
    if (currentHour.value === 12) value.setHours(pm.value ? 12 : 0);
    else value.setHours(pm.value ? currentHour.value + 12 : currentHour.value);
  } else {
    value.setHours(currentHour.value);
  }

  value.setMinutes(currentMinute.value);
  value.setSeconds(currentSecond.value);

  if (isArraySelection(calendarModelValue.value)) {
    if (isMultipleSelection()) {
      value = [...calendarModelValue.value];
    } else if (isRangeSelection()) {
      if (calendarModelValue.value[getLastIndex(calendarModelValue.value)])
        value = [calendarModelValue.value[0], value];
      else value = [value, null];
    }
  }

  updateModel(value);
  emit('dateSelect', value);
  setTimeout(() => (timePickerChange.value = false), 0);
};

const getMillisTime = (value?: Date | Date[]): number | number[] => {
  // eslint-disable-next-line no-nested-ternary
  const millisValue = value
    ? Array.isArray(value)
      ? value.map((v) => v?.getTime())
      : value.getTime()
    : null;

  return millisValue;
};

const updateModel = (value?: Date | Date[]): void => {
  if (props.showButtons || props.showTime) {
    temporaryDate.value = value;
    changesSaved.value = false;
  } else {
    emit('update:epochTimeMillis', getMillisTime(value));
    emit('update:modelValue', value);
  }
};

const applyChanges = (): void => {
  emit('update:epochTimeMillis', getMillisTime(temporaryDate.value));
  emit('update:modelValue', temporaryDate.value);
  changesSaved.value = true;
  hidePanel();
};

const revertChanges = (): void => {
  temporaryDate.value = props.modelValue;
  changesSaved.value = true;
  hidePanel();
};

const toggleAMPM = (): void => {
  const validHour = validateTime(
    currentHour.value,
    currentMinute.value,
    currentSecond.value,
    !pm.value,
  );

  if (!validHour && (props.maxDate || props.minDate)) return;

  pm.value = !pm.value;
  updateModelTime();
};

const updateCurrentMetaData = (): void => {
  currentMonth.value = viewDate.value.getMonth();
  currentYear.value = viewDate.value.getFullYear();

  if (props.showTime) {
    updateCurrentTimeMeta(viewDate.value);
  }
};

const updateCurrentTimeMeta = (date: Date): void => {
  let hour = date.getHours();

  if (props.hourFormat === '12') {
    pm.value = hour > 11;

    if (hour >= 12) hour = hour == 12 ? 12 : hour - 12;
    else hour = hour == 0 ? 12 : hour;
  }

  currentHour.value = Math.floor(hour / props.stepHour) * props.stepHour;
  currentMinute.value =
    Math.floor(date.getMinutes() / props.stepMinute) * props.stepMinute;
  /*
   * CurrentSecond.value =
   *   Math.floor(date.getSeconds() / props.stepSecond) * props.stepSecond;
   */
};

const formatValue = (): string => {
  const value =
    props.showButtons || props.showTime
      ? temporaryDate.value
      : calendarModelValue.value;

  if (typeof value === 'string') {
    return value;
  }

  let formattedValue = '';

  if (value) {
    try {
      if (isSingleSelection(value)) {
        if (value.toString() !== 'Invalid Date')
          formattedValue = formatDateTime(value);
      } else if (isMultipleSelection()) {
        if (value && value.length) {
          formattedValue = value.map((date) => formatDateTime(date)).join(', ');
        }
      } else if (isRangeSelection()) {
        if (value && value.length) {
          const startDate = value[0];
          const endDate = value[getLastIndex(value)];

          if (startDate.toString() !== 'Invalid Date') {
            formattedValue = formatDateTime(startDate);
          }

          if (endDate && endDate.toString() !== 'Invalid Date') {
            formattedValue += ' - ' + formatDateTime(endDate);
          }
        }
      }
    } catch {
      formattedValue = value.toString();
    }
  }

  return formattedValue;
};

const formatDateTime = (date: Date): string | null => {
  let formattedValue: string | null = null;

  if (date) {
    formattedValue = formatDate(date, datePattern.value);

    if (props.showTime) {
      formattedValue += ' ' + formatTime(date);
    }
  }

  return formattedValue;
};

const formatDate = (date: Date, format: string): string => {
  if (!date) {
    return '';
  }

  let iFormat: number;

  const lookAhead = (match: string): boolean => {
    const matches =
      iFormat + 1 < format.length && format.charAt(iFormat + 1) === match;

    if (matches) {
      iFormat++;
    }

    return matches;
  };

  const formatNumber = (match: string, value: number, len: number): string => {
    let num = '' + value;

    if (lookAhead(match)) {
      while (num.length < len) {
        num = '0' + num;
      }
    }

    return num;
  };

  const formatName = (
    match: string,
    value: number,
    shortNames: string[],
    longNames: string[],
  ): string => {
    return lookAhead(match) ? longNames[value] : shortNames[value];
  };

  let output = '';
  let literal = false;

  if (date) {
    for (iFormat = 0; iFormat < format.length; iFormat++) {
      if (literal) {
        if (format.charAt(iFormat) === "'" && !lookAhead("'")) {
          literal = false;
        } else {
          output += format.charAt(iFormat);
        }
      } else {
        switch (format.charAt(iFormat)) {
          case 'd':
            output += formatNumber('d', date.getDate(), 2);
            break;
          case 'D':
            output += formatName(
              'D',
              date.getDay(),
              config.locale.global.dayNamesShort,
              config.locale.global.dayNames,
            );
            break;
          case 'o':
            output += formatNumber(
              'o',
              Math.round(
                (new Date(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate(),
                ).getTime() -
                  new Date(date.getFullYear(), 0, 0).getTime()) /
                  86400000,
              ),
              3,
            );
            break;
          case 'm':
            output += formatNumber('m', date.getMonth() + 1, 2);
            break;
          case 'M':
            output += formatName(
              'M',
              date.getMonth(),
              config.locale.global.monthNamesShort,
              config.locale.global.monthNames,
            );
            break;
          case 'y':
            output += lookAhead('y')
              ? date.getFullYear()
              : (date.getFullYear() % 100 < 10 ? '0' : '') +
                (date.getFullYear() % 100);
            break;
          case '@':
            output += date.getTime();
            break;
          case '!':
            output += date.getTime() * 10000 + ticksTo1970;
            break;
          case "'":
            if (lookAhead("'")) {
              output += "'";
            } else {
              literal = true;
            }

            break;
          default:
            output += format.charAt(iFormat);
        }
      }
    }
  }

  return output;
};

const formatTime = (date: Date): string => {
  if (!date) {
    return '';
  }

  let output = '';
  let hours = date.getHours();
  const minutes = date.getMinutes();

  if (props.hourFormat === '12' && hours > 11 && hours !== 12) {
    hours -= 12;
  }

  if (props.hourFormat === '12') {
    // eslint-disable-next-line no-nested-ternary
    output += hours === 0 ? 12 : hours < 10 ? '0' + hours : hours;
  } else {
    output += hours < 10 ? '0' + hours : hours;
  }

  output += ':';
  output += minutes < 10 ? '0' + minutes : minutes;

  if (props.hourFormat === '12') {
    output +=
      date.getHours() > 11
        ? ` ${config.locale.global.pm}`
        : ` ${config.locale.global.am}`;
  }

  return output;
};

const convertDateTimeZone = (dateMeta: CalendarDate): Date => {
  const { year, month, day } = dateMeta;
  const userStorage = JSON.parse(localStorage.getItem('user') || '{}');
  const userTimeZone = userStorage?.generalSetting?.timezone;

  if (userTimeZone && DateTime.local().setZone(userTimeZone).isValid) {
    const dateTime = DateTime.fromObject(
      { year, month: month + 1, day, hour: 0 },
      { zone: userTimeZone },
    );
    return dateTime.toUTC().toJSDate(); // Convert to UTC Date
  }

  // Fallback to local time if no timezone is set or invalid
  return new Date(year, month, day);
};

const selectDate = (dateMeta: CalendarDate): void => {
  let date = convertDateTimeZone(dateMeta);

  if (props.showTime) {
    date.setHours(
      currentHour.value +
        (props.hourFormat === '12' && currentHour.value !== 12 && pm.value
          ? 12
          : 0),
    );

    date.setMinutes(currentMinute.value);
    date.setSeconds(currentSecond.value);
  }

  // Min Date Validation
  if (props.minDate && props.minDate > date) {
    date = props.minDate;
    currentHour.value = date.getHours();
    currentMinute.value = date.getMinutes();
    currentSecond.value = date.getSeconds();
  }

  // Max Date Validation
  if (props.maxDate && props.maxDate < date) {
    date = props.maxDate;
    currentHour.value = date.getHours();
    currentMinute.value = date.getMinutes();
    currentSecond.value = date.getSeconds();
  }

  let modelVal: Date[] | Date | null = null;

  if (isSingleSelection(calendarModelValue.value)) {
    modelVal = date;
  } else if (isMultipleSelection()) {
    let selectedDates = calendarModelValue.value;

    if (selectedDates && selectedDates.length) {
      const dateIndex = selectedDates.findIndex(
        (each) =>
          each.getFullYear() === date.getFullYear() &&
          each.getMonth() === date.getMonth() &&
          each.getDate() === date.getDate(),
      );

      // Unselect the date if it's already selected
      if (dateIndex !== -1) {
        selectedDates.splice(dateIndex, 1);
      } else selectedDates = [...selectedDates, date];

      // Sort the dates, oldest to newest
      selectedDates.sort((a, b) => a.getTime() - b.getTime());
    } else selectedDates = [date];
    modelVal = selectedDates;
  }
  // Range Selection with props `exactSelection`
  else if (props.exactSelection) {
    modelVal = formattingExactSelection(dateMeta, date);
  }
  // Normal Range Selection
  else if (isRangeSelection()) {
    if (calendarModelValue.value && calendarModelValue.value.length) {
      let startDate = calendarModelValue.value[0];
      let endDate =
        calendarModelValue.value[getLastIndex(calendarModelValue.value)];

      if (!endDate && date.getTime() >= startDate.getTime()) {
        endDate = date;
      } else {
        startDate = date;
        endDate = null;
      }

      modelVal = [startDate, endDate];
    } else {
      modelVal = [date, null];
    }
  }

  // Update the model if the value is valid
  if (modelVal !== null) {
    updateModel(modelVal);
  }

  // Hide overlay for range selection after selection is complete
  hideOverlayRangeSelection(modelVal as Date[]);

  // Emit the date select event
  emit('dateSelect', date);
};

/**
 * Function for conditioning to hide overlay for range selection
 * To avoid complexity in function `selectDate`
 *
 * @param modelVal
 */
const hideOverlayRangeSelection = (modelVal: Date[]): void => {
  const isCompleteRangeSelection = !props.exactSelection
    ? modelVal && modelVal[getLastIndex(modelVal as Date[])] !== null
    : (modelVal as Date[]).length > 1;

  if (
    isRangeSelection() &&
    props.hideOnRangeSelection &&
    !props.showButtons &&
    !props.showTime &&
    isCompleteRangeSelection
  ) {
    hidePanel();
  }
};

/**
 * Generates an array of `Date` objects representing a continuous selection between two dates in range mode,
 * including all dates between the first and second click, across months if necessary.
 *
 * This function assumes that the first selected date (`startDate`) is stored in `previousDateMeta`,
 * and uses `datesInMonth` and `previousDatesInMonth` to determine the full range.
 *
 * @param {CalendarDate} dateMeta - The metadata of the newly selected date (e.g., day, month, year).
 * @param {Date} date - The actual `Date` object corresponding to `dateMeta`.
 * @returns {Date[]} An array of `Date` objects covering the full selected range, including the start and end dates.
 *
 * @remarks
 * - If the same date is clicked twice, it resets the selection to that single date.
 * - Handles selections across month boundaries.
 * - Relies on the external state: `previousDateMeta`, `datesInMonth`, and `previousDatesInMonth`.
 *
 * @example - rough examples of return
 * // Selecting from March 1 to March 5 returns [Mar 1, Mar 2, Mar 3, Mar 4, Mar 5]
 * // Selecting from March 30 to April 2 spans months and returns the appropriate sequence
 */
const formattingExactSelection = (
  dateMeta: CalendarDate,
  date: Date,
): Date[] => {
  const startDate = previousDateMeta.value;

  // Stores start date and return to update model value
  if (!previousDateMeta.value || startDate.day === dateMeta.day) {
    previousDateMeta.value = dateMeta;
    previousDatesInMonth.value = datesInMonth.value;
    return [date];
  }

  // If end date is selected, handle value of dates between start date and end date (also handle when other month was involved)
  const restDate =
    startDate.month === dateMeta.month
      ? datesInMonth.value
          .filter((each) => each.day > startDate.day && each.day < dateMeta.day)
          .map((each) => new Date(each.year, each.month, each.day))
      : [
          ...previousDatesInMonth.value
            .filter((each) => !each.otherMonth && each.day > startDate.day)
            .map((each) => new Date(each.year, each.month, each.day)),
          ...datesInMonth.value
            .filter((each) => !each.otherMonth && each.day < dateMeta.day)
            .map((each) => new Date(each.year, each.month, each.day)),
        ];

  return [
    new Date(startDate.year, startDate.month, startDate.day),
    ...restDate,
    date,
  ];
};

const hidePanel = (): void => {
  setTimeout(() => {
    /*
     * TODO: add Focus to input here
     * TODO: overlayVisible = false;
     */
    overlayPanel.value?.hide();
  }, 150);
};

watch(
  () => [
    props.minDate,
    props.maxDate,
    props.excludeDates,
    props.maxYear,
    props.maxMonth,
  ],
  () => updateCalendar(),
);

watch(
  () => calendarModelValue.value,
  () => {
    updateCurrentMetaData();
    inputValue.value = formatValue();
  },
  {
    immediate: true,
  },
);

if (props.showButtons || props.showTime) {
  watch(
    temporaryDate,
    () => {
      updateCurrentMetaData();
      inputValue.value = formatValue();
    },
    {
      immediate: true,
      deep: true,
    },
  );
}
</script>

<template>
  <FieldWrapper
    ref="wrapper"
    :info="fieldInfo"
    :label="label"
    :mandatory="mandatory"
    :show-optional-text="showOptionalText"
  >
    <slot :on-click="onInputClick">
      <InputGroup :disabled="disabled" :invalid="invalidInput">
        <span
          v-bind="$preset.calendar.root({ props })"
          :id="id"
          data-wv-name="calendar"
          data-wv-section="root"
        >
          <input
            :id="inputId"
            ref="input"
            :aria-invalid="invalid || undefined"
            :disabled="disabled"
            :placeholder="placeholder ?? config.locale.defaultPlaceholder"
            :tabindex="0"
            :value="inputValue"
            @click="onInputClick"
            aria-autocomplete="none"
            autocomplete="off"
            inputmode="none"
            readonly
            role="combobox"
            type="text"
            v-bind="$preset.calendar.input({ props })"
          />

          <Icon
            @click="onButtonTriggerClick"
            icon="calendar"
            v-bind="$preset.calendar.inputicon"
          />
        </span>
      </InputGroup>
    </slot>

    <OverlayPanel
      ref="overlayPanel"
      :pt="{
        root: {
          class: [$preset.overlaypanel.root.class, '!mt-0', props.overlayClass],
        },
      }"
      @hide="onPanelHide"
    >
      <div v-bind="$preset.calendar.panel({ props })" data-wv-section="panel">
        <div v-bind="$preset.calendar.container" data-wv-section="container">
          <div
            v-if="!hideNavigator"
            v-bind="$preset.calendar.header({ props, state: { currentView } })"
            data-wv-section="navigation"
          >
            <Button
              @click="navBackward"
              class="rotate-180 !p-1"
              data-wv-section="prevbutton"
              icon="arrow-right"
              icon-class="w-5 h-5"
              severity="secondary"
              text
            />

            <div class="flex gap-1">
              <button
                v-if="currentView == 'date'"
                v-bind="$preset.calendar.monthTitle"
                @click="currentView = 'month'"
              >
                {{
                  $wangsvue.config.locale.global.monthNamesShort[currentMonth]
                }}
              </button>

              <button
                v-if="currentView != 'year' && props.showYear"
                v-bind="$preset.calendar.yearTitle({ props })"
                @click="currentView = 'year'"
              >
                {{ currentYear }}
              </button>

              <span v-if="currentView == 'year'">
                {{ yearPickerValues[0].value }} -
                {{ yearPickerValues[yearPickerValues.length - 1].value }}
              </span>
            </div>

            <Button
              @click="navForward"
              class="!p-1"
              data-wv-section="nextbutton"
              icon="arrow-right"
              icon-class="w-5 h-5"
              severity="secondary"
              text
            />
          </div>

          <div
            v-if="currentView === 'date'"
            class="flex flex-col"
            data-wv-section="datepicker"
          >
            <div data-wv-section="tableheader">
              <div
                class="grid grid-cols-7 justify-between h-[26.8px] items-center"
                data-wv-section="weekdays"
              >
                <span
                  :key="day"
                  v-for="day in $wangsvue.config.locale.global.dayNamesShort"
                  abbr="day"
                  class="text-center"
                  data-wv-section="weekday"
                >
                  {{ day }}
                </span>
              </div>
            </div>

            <div class="grid grid-cols-7" data-wv-section="tablebody">
              <span
                :key="dayIndex"
                v-for="(date, dayIndex) in datesInMonth"
                :aria-label="date.day.toString()"
                :data-wv-other-month="date.otherMonth"
                :data-wv-today="date.today"
                data-wv-section="day"
              >
                <Skeleton
                  v-if="isLoading && !date.otherMonth"
                  class="!w-8 !h-8"
                  shape="circle"
                />

                <span
                  v-else-if="!isLoading"
                  :aria-disabled="!date.selectable"
                  :data-p-disabled="date.otherMonth || !date.selectable"
                  @click="onDateSelect($event, date)"
                  v-bind="
                    $preset.calendar.daylabel({
                      context: {
                        date,
                        today: date.today,
                        otherMonth: date.otherMonth,
                        selected: isSelected(date),
                        firstSelected: isFirstSelected(date),
                        lastSelected: isLastSelected(date),
                        disabled: !date.selectable,
                      },
                    })
                  "
                  data-wv-section="daylabel"
                  draggable="false"
                  tabindex="0"
                >
                  <template v-if="!date.otherMonth">{{ date.day }}</template>
                </span>
              </span>
            </div>
          </div>

          <div
            v-if="currentView === 'month'"
            :class="
              props.showYear
                ? $preset.calendar.monthpicker.class
                : 'w-full grid grid-cols-3 gap-y-1 gap-x-0 mt-1'
            "
            data-wv-section="monthpicker"
          >
            <span
              :key="m.value"
              v-for="(m, i) of monthPickerValues"
              v-bind="
                $preset.calendar.month({
                  context: {
                    month: m,
                    monthIndex: i,
                    selected: isMonthSelected(i),
                    disabled: !m.selectable,
                  },
                })
              "
              :data-p-disabled="!m.selectable"
              :data-p-highlight="isMonthSelected(i)"
              @click="onMonthSelect($event, m)"
            >
              {{ $wangsvue.config.locale.global.monthNamesShort[m.value] }}
            </span>
          </div>

          <div
            v-if="currentView === 'year'"
            v-bind="$preset.calendar.yearpicker"
          >
            <span
              :key="y.value"
              v-for="y of yearPickerValues"
              v-bind="
                $preset.calendar.year({
                  context: {
                    year: y,
                    selected: isYearSelected(y.value),
                    disabled: !y.selectable,
                  },
                })
              "
              :data-p-disabled="!y.selectable"
              :data-p-highlight="isYearSelected(y.value)"
              @click="onYearSelect($event, y)"
            >
              {{ y.value }}
            </span>
          </div>
        </div>

        <div
          v-if="showTime && currentView === 'date'"
          v-bind="$preset.calendar.timepicker"
          data-wv-section="timepicker"
        >
          <!-- Hour Picker -->

          <div
            v-bind="$preset.calendar.hourpicker"
            data-wv-section="hourpicker"
          >
            <button
              :aria-label="$wangsvue.config.locale.global.nextHour"
              @keydown="onContainerButtonKeydown"
              @keydown.enter="onTimePickerElementMouseDown($event, 0, 1)"
              @keydown.space="onTimePickerElementMouseDown($event, 0, 1)"
              @keyup.enter="onTimePickerElementMouseUp($event)"
              @keyup.space="onTimePickerElementMouseUp($event)"
              @mousedown="onTimePickerElementMouseDown($event, 0, 1)"
              @mouseleave="onTimePickerElementMouseLeave()"
              @mouseup="onTimePickerElementMouseUp($event)"
              v-bind="$preset.calendar.incrementbutton"
              data-wv-group-section="hourpicker"
              data-wv-section="incrementbutton"
              type="button"
            >
              <slot name="incrementicon">
                <Icon
                  data-wv-group-section="hourpicker"
                  data-wv-section="incrementicon"
                  icon="arrow-up-s"
                  v-bind="$preset.calendar.incrementicon"
                />
              </slot>
            </button>

            <span
              v-bind="$preset.calendar.timepickerlabel"
              data-wv-group-section="hourpicker"
              data-wv-section="timepickerlabel"
            >
              {{ formattedCurrentHour }}
            </span>

            <button
              :aria-label="$wangsvue.config.locale.global.prevHour"
              @keydown="onContainerButtonKeydown"
              @keydown.enter="onTimePickerElementMouseDown($event, 0, -1)"
              @keydown.space="onTimePickerElementMouseDown($event, 0, -1)"
              @keyup.enter="onTimePickerElementMouseUp($event)"
              @keyup.space="onTimePickerElementMouseUp($event)"
              @mousedown="onTimePickerElementMouseDown($event, 0, -1)"
              @mouseleave="onTimePickerElementMouseLeave()"
              @mouseup="onTimePickerElementMouseUp($event)"
              v-bind="$preset.calendar.decrementbutton"
              data-wv-group-section="hourpicker"
              data-wv-section="decrementbutton"
              type="button"
            >
              <slot name="decrementicon">
                <Icon
                  data-wv-group-section="hourpicker"
                  data-wv-section="decrementicon"
                  icon="arrow-down"
                  v-bind="$preset.calendar.decrementicon"
                />
              </slot>
            </button>
          </div>

          <!-- TIme Separator -->
          <div
            v-bind="$preset.calendar.separatorcontainer"
            data-wv-section="separatorcontainer"
          >
            <span
              v-bind="$preset.calendar.separator"
              data-wv-section="separator"
            >
              {{ timeSeparator }}
            </span>
          </div>

          <!-- Minute Picker -->

          <div
            v-bind="$preset.calendar.minutepicker"
            data-wv-section="minutepicker"
          >
            <button
              :aria-label="$wangsvue.config.locale.global.nextMinute"
              :disabled="disabled"
              @keydown="onContainerButtonKeydown"
              @keydown.enter="onTimePickerElementMouseDown($event, 1, 1)"
              @keydown.space="onTimePickerElementMouseDown($event, 1, 1)"
              @keyup.enter="onTimePickerElementMouseUp($event)"
              @keyup.space="onTimePickerElementMouseUp($event)"
              @mousedown="onTimePickerElementMouseDown($event, 1, 1)"
              @mouseleave="onTimePickerElementMouseLeave()"
              @mouseup="onTimePickerElementMouseUp($event)"
              v-bind="$preset.calendar.incrementbutton"
              data-wv-group-section="minutepicker"
              data-wv-section="incrementbutton"
              type="button"
            >
              <slot name="incrementicon">
                <Icon
                  data-wv-group-section="minutepicker"
                  data-wv-section="incrementicon"
                  icon="arrow-up-s"
                  v-bind="$preset.calendar.incrementicon"
                />
              </slot>
            </button>

            <span
              v-bind="$preset.calendar.timepickerlabel"
              data-wv-section="timepickerlabel"
            >
              {{ formattedCurrentMinute }}
            </span>

            <button
              :aria-label="$wangsvue.config.locale.global.prevMinute"
              :disabled="disabled"
              @keydown="onContainerButtonKeydown"
              @keydown.enter="onTimePickerElementMouseDown($event, 1, -1)"
              @keydown.space="onTimePickerElementMouseDown($event, 1, -1)"
              @keyup.enter="onTimePickerElementMouseUp($event)"
              @keyup.space="onTimePickerElementMouseUp($event)"
              @mousedown="onTimePickerElementMouseDown($event, 1, -1)"
              @mouseleave="onTimePickerElementMouseLeave()"
              @mouseup="onTimePickerElementMouseUp($event)"
              v-bind="$preset.calendar.decrementbutton"
              data-wv-group-section="minutepicker"
              data-wv-section="decrementbutton"
              type="button"
            >
              <slot name="decrementicon">
                <Icon
                  data-wv-group-section="minutepicker"
                  data-wv-section="decrementicon"
                  icon="arrow-down"
                  v-bind="$preset.calendar.decrementicon"
                />
              </slot>
            </button>
          </div>

          <!-- AM/PM Picker -->
          <SelectButton
            v-if="hourFormat === '12'"
            :model-value="pm"
            :options="[
              { label: 'AM', value: false },
              { label: 'PM', value: true },
            ]"
            @update:model-value="toggleAMPM"
            option-label="label"
            option-value="value"
          />
        </div>

        <slot name="addon-overlay" />

        <div
          v-if="showButtons || showTime"
          v-show="currentView === 'date'"
          class="w-full flex items-center justify-end gap-1"
          data-wv-section="calendarbuttons"
        >
          <Button
            :label="config.locale.cancelFooterButtonLabel"
            @click="revertChanges"
            severity="secondary"
            text
          />

          <Button
            :disabled="!calendarModelValue"
            :label="config.locale.applyFooterButtonLabel"
            @click="applyChanges"
          />
        </div>
      </div>
    </OverlayPanel>

    <ValidatorMessage :message="$attrs['error-message'] as string" />
  </FieldWrapper>
</template>
